import {
  Check,
  ArrowRight,
  Users,
  QrCode,
  Link2,
  Star,
  Clock,
  UserPlus,
  Globe,
  Briefcase,
  Building2,
  Share2,
  BadgeCheck,
  Zap,
  Home,
  Calculator,
  MessageSquare,
  Image,
  FileText,
  Mail,
  Monitor,
  Settings,
  Headphones,
  ShieldCheck,
  Lightbulb,
  ChevronDown,
  LineChart,
  Shield,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import realEstateDiscover from "@/assets/real-estate-discover-new-2.png";
import realEstateCompetitiveEdge from "@/assets/real-estate-competitive-edge-new.png";
import ComparisonGridSection from "@/components/landing/ComparisonGridSection";
import companyLogos from "@/assets/company-logos.png";
import testimonialAaron from "@/assets/testimonial-aaron.png";
import testimonialTodd from "@/assets/testimonial-todd.png";
import testimonialNikki from "@/assets/testimonial-nikki.png";
import testimonialAnis from "@/assets/testimonial-anis.png";
import photoContactVideoNew from "@/assets/feature-photo-contact-video.mp4";
import analyticsVideoNew from "@/assets/feature-analytics-new.mp4";
import socialLinksVideoNew from "@/assets/social-media-links-v2.mp4";
import featureQrCodeVideo from "@/assets/qr-code-video.mp4";
import featureAddToContactsVideo from "@/assets/add-to-contacts-video.mp4";
import featureRichProfileVideo from "@/assets/rich-profile-content-video.mp4";
import colleaguesDemo from "@/assets/colleagues-demo.gif";
import featureTapToContactVideo from "@/assets/tap-to-contact-video.mp4";
import featureEmailFooterImage from "@/assets/email-footer-image.png";
import featureZoomBackgroundVideo from "@/assets/zoom-background-video.mp4";

const REAL_ESTATE_IMAGE_SRC = `${import.meta.env.BASE_URL}assets/real-estate-profile-screenshot.jpeg`;

const features = [
  {
    icon: Image,
    title: "Photo & Contact Info",
    description: "Professional headshot with all your contact details, Company role & About me section",
    image: photoContactVideoNew,
    isVideo: true,
  },
  {
    icon: Briefcase,
    title: "Analytics",
    description: "Track networking impact with profile views, sources, and saved contacts",
    image: analyticsVideoNew,
    isVideo: true,
  },
  {
    icon: Globe,
    title: "Social & Website Links",
    description: "Connect all your platforms in one place",
    image: socialLinksVideoNew,
    isVideo: true,
  },
  {
    icon: QrCode,
    title: "QR Code",
    description: "Instant sharing with a simple scan",
    image: featureQrCodeVideo,
    isVideo: true,
  },
  {
    icon: Link2,
    title: "Tap To Contact",
    description: "More contact options increase customer engagement rates",
    image: featureTapToContactVideo,
    isVideo: true,
  },
  {
    icon: UserPlus,
    title: "Instant Add-to-Contacts",
    description: "One tap to save you to their phone",
    image: featureAddToContactsVideo,
    isVideo: true,
  },
  {
    icon: FileText,
    title: "Rich Profile Content",
    description: "Tailor your profile to your needs",
    image: featureRichProfileVideo,
    isVideo: true,
  },
  {
    icon: Share2,
    title: "Colleagues",
    description: "Show team members and access QCard profiles",
    image: colleaguesDemo,
  },
  {
    icon: Mail,
    title: "Email Footers",
    description: "Add your QCard link to every email signature",
    image: featureEmailFooterImage,
    isVideo: false,
  },
  {
    icon: Monitor,
    title: "Video Conference Background",
    description: "Brand your video calls with your QCard QR code",
    image: featureZoomBackgroundVideo,
    isVideo: true,
  },
];

const enterpriseFeatures = [
  {
    icon: Settings,
    title: "Integrates With Your Enterprise",
    color: "bg-blue-100 text-blue-600",
    items: [
      "Integrations with enterprise HRMS and CRM systems",
      "Unified and controlled platform to manage employee network and access",
      "APIs",
      "Custom URLs tailored to your company's domain",
    ],
  },
  {
    icon: Headphones,
    title: "Enterprise-Level Customer Support",
    color: "bg-orange-100 text-orange-500",
    items: [
      "Seamless and quick user onboarding and system implementation",
      "Employee training on how to use the platform",
      "24/7 Helpdesk",
      "360-Degree Customer Success Team",
      "Personal Concierge",
      "Rollout support",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Secure",
    color: "bg-pink-100 text-pink-500",
    items: ["Single Sign-On", "Enterprise-grade security", "On Prem/Cloud Solutions"],
  },
  {
    icon: Lightbulb,
    title: "Custom Solutions",
    color: "bg-green-100 text-green-500",
    items: [
      "Collaborate with the QCard team to tailor solutions for the specific needs of your enterprise",
      "In-house experts for expanded customer experience solutions",
    ],
  },
];

const steps = [
  {
    step: "01",
    title: "Create Your Free QCard",
    description: "Sign up in seconds — no credit card needed",
  },
  {
    step: "02",
    title: "Customize Your Profile",
    description: "Add your photo, bio, links, and contact info",
  },
  {
    step: "03",
    title: "Share Instantly",
    description: "Use your QR code or personal link anywhere",
  },
];

const testimonials = [
  {
    image: testimonialAaron,
    name: "Aaron Pyman",
    company: "Redfin",
    link: "https://www.linkedin.com/in/aaronpyman/",
  },
  {
    image: testimonialAnis,
    name: "Anis Dave",
    company: "from - The Digital Transformation Agency",
    link: "https://www.linkedin.com/in/aaronpyman/",
  },
  {
    image: testimonialNikki,
    name: "Nikki Beauchamp",
    company: "Sotheby's International Realty",
    link: "https://www.linkedin.com/in/nicolebeauchamp/",
  },
  {
    image: testimonialTodd,
    name: "Todd Edwards",
    company: "Redfin",
    link: "https://www.linkedin.com/in/rtoddedwards/",
  },
];

const RealEstateDigitalBusinessCard = () => {
  const [selectedFeature, setSelectedFeature] = useState(0);
  const [isEnterpriseExpanded, setIsEnterpriseExpanded] = useState(false);

  return (
    <div className="bg-background text-foreground min-h-screen">
      {/* Hero Section */}
      <section id="signup-section" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/50 via-background to-accent/30" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-enterprise-purple/15 via-purple-200/10 to-transparent rounded-full blur-3xl -translate-y-1/3 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-orange-200/15 via-amber-100/10 to-transparent rounded-full blur-3xl translate-y-1/4 -translate-x-1/4" />
        <div className="absolute top-20 left-[15%] w-2 h-2 bg-enterprise-purple/30 rounded-full" />
        <div className="absolute top-32 right-[20%] w-3 h-3 bg-orange-300/25 rounded-full" />
        <div className="absolute bottom-24 left-[25%] w-1.5 h-1.5 bg-purple-300/40 rounded-full" />
        <div className="absolute top-1/4 left-0 w-32 md:w-48 h-[1px] bg-gradient-to-r from-transparent via-enterprise-purple/30 to-transparent" />
        <div className="absolute bottom-1/3 right-0 w-24 md:w-40 h-[1px] bg-gradient-to-l from-transparent via-orange-300/25 to-transparent" />
        <div className="absolute top-12 right-12 w-20 h-20 border border-enterprise-purple/15 rounded-full" />
        <div className="absolute bottom-16 left-8 w-16 h-16 border border-orange-200/10 rounded-lg rotate-12" />

        <div className="container relative z-10 mx-auto px-4 sm:px-6 py-16 md:py-24">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-6 items-start max-w-full overflow-hidden">
            <div className="text-center lg:text-left w-full max-w-full px-2 sm:px-0 lg:pt-24 order-1 lg:order-none flex flex-col">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-2 text-foreground">
                In Real Estate, Speed Wins. <span className="text-gradient">QCard Keeps You One Step Ahead.</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-4">
                A free digital business card built for Real Estate Professionals.
              </p>
              <p className="inline-flex items-center justify-center lg:justify-start gap-2 text-base font-bold mb-6 bg-gradient-to-r from-enterprise-purple to-enterprise-purple-dark bg-clip-text text-transparent w-full">
                FREE Forever — No Credit Card Required
              </p>

              <div className="lg:hidden flex flex-col items-center justify-start w-full max-w-full mb-8">
                <div className="relative w-[280px] h-[580px] bg-slate-900 rounded-[3rem] p-3 shadow-2xl border border-border">
                  <div className="w-full h-full rounded-[2.5rem] overflow-y-auto scroll-smooth">
                    <img
                      src={REAL_ESTATE_IMAGE_SRC}
                      alt="QCard Real Estate digital business card preview"
                      className="w-full h-auto block"
                      loading="eager"
                      decoding="async"
                      onError={(e) => {
                        const img = e.currentTarget;
                        if (img.dataset.fallbackApplied === "true") return;
                        img.dataset.fallbackApplied = "true";
                        img.src = "/assets/real-estate-profile-screenshot.jpeg";
                      }}
                    />
                  </div>
                </div>
              </div>

              <p className="text-lg text-muted-foreground mb-4 max-w-xl mx-auto lg:mx-0">
                Share property details, contact info, and location in seconds — making every showing and open house an
                opportunity to connect.
              </p>

              <div className="mb-8 text-base text-muted-foreground max-w-xl mx-auto lg:mx-0">
                <p className="font-medium text-foreground mb-2 text-lg">Designed for:</p>
                <ul className="space-y-1.5">
                  <li>• Open houses and property showings</li>
                  <li>• Networking events and broker opens</li>
                  <li>• Anywhere first impressions matter</li>
                </ul>
              </div>

              <div className="flex flex-wrap items-center gap-4 justify-center lg:justify-start text-base text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-enterprise-purple" />
                  <span>Feature Rich</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-enterprise-purple" />
                  <span>60-second setup</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-enterprise-purple" />
                  <span>No Ads</span>
                </div>
              </div>
            </div>

            <div className="hidden lg:flex flex-col items-center justify-start order-2 lg:order-none w-full max-w-full">
              <div className="relative w-[300px] h-[640px] bg-slate-900 rounded-[3rem] p-3 shadow-2xl border border-border">
                <div className="w-full h-full rounded-[2.5rem] overflow-y-auto scroll-smooth">
                  <img
                    src={REAL_ESTATE_IMAGE_SRC}
                    alt="QCard Real Estate digital business card preview"
                    className="w-full h-auto block"
                    loading="eager"
                    decoding="async"
                    onError={(e) => {
                      const img = e.currentTarget;
                      if (img.dataset.fallbackApplied === "true") return;
                      img.dataset.fallbackApplied = "true";
                      img.src = "/assets/real-estate-profile-screenshot.jpeg";
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-start items-center lg:items-end w-full max-w-full order-3 lg:order-none">
              <div className="bg-card rounded-2xl p-4 shadow-2xl border border-border w-full max-w-md overflow-hidden mx-auto lg:mx-0">
                <div className="flex items-center justify-center min-h-[60px] mb-4">
                  <h2 className="text-xl font-bold text-center text-foreground">
                    Real Estate Agents Sign up for your
                    <br />
                    FREE Digital Business Card
                  </h2>
                </div>
                <div
                  className="w-full rounded-xl overflow-hidden flex justify-center touch-none"
                  style={{
                    height: "630px",
                    overflow: "hidden",
                    WebkitOverflowScrolling: "auto",
                  }}
                >
                  <iframe
                    src="https://qa.qcardenterprise.com/createNewUserAccount"
                    className="w-full max-w-[370px]"
                    height="730"
                    title="QCard Enterprise Sign Up Form"
                    loading="lazy"
                    style={{
                      border: "none",
                      overflow: "hidden",
                      pointerEvents: "auto",
                    }}
                    scrolling="no"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Logos */}
      <section className="py-12 bg-muted/30 border-y border-border">
        <div className="container mx-auto px-4">
          <p className="text-center text-muted-foreground mb-8 text-sm font-medium">
            Trusted by professionals at leading companies
          </p>
          <img
            src={companyLogos}
            alt="Trusted by Redfin, Keller Williams, UBS, S&P Global, Sotheby's, Compass, and more"
            className="max-w-4xl mx-auto w-full"
          />
        </div>
      </section>

      {/* Discover QCard Section */}
      <section className="relative py-16 md:py-24 bg-muted/30 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <img
                src={realEstateDiscover}
                alt="QCard digital business card for real estate professionals"
                className="w-full h-auto max-w-lg lg:max-w-3xl mx-auto rounded-2xl"
                loading="lazy"
                decoding="async"
              />
            </div>

            <div className="lg:w-1/2 text-center lg:text-left">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">Discover QCard</h2>
              <p className="text-lg text-muted-foreground mb-4">
                QCard is a modern digital business card and branding tool tailored for real estate professionals. Each
                agent can build an elegant online profile featuring their contact details, listings, and market
                expertise.
              </p>
              <p className="text-lg text-muted-foreground mb-8">
                QCard goes beyond a business card, serving as a branded profile where prospects and buyers can find all
                your professional information in one place.
              </p>
              <div className="flex flex-col items-center lg:items-start">
                <Button
                  className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-4 h-auto rounded-lg shadow-lg hover:shadow-xl"
                  onClick={() => document.getElementById("signup-section")?.scrollIntoView({ behavior: "smooth" })}
                >
                  Create Your Digital Card Free
                </Button>
                <div className="mt-3 flex flex-col sm:flex-row items-center gap-2 text-sm text-muted-foreground">
                  <span>No credit card required</span>
                  <span className="hidden sm:inline">•</span>
                  <span>Takes less than 2 minutes</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Competitive Edge Section */}
      <section className="relative py-16 md:py-24 bg-muted/30 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-3/5">
              <img
                src={realEstateCompetitiveEdge}
                alt="Real estate agents using QCard digital business cards"
                className="w-full h-auto max-w-lg lg:max-w-3xl mx-auto rounded-2xl"
                loading="lazy"
                decoding="async"
              />
            </div>

            <div className="lg:w-2/5 text-center lg:text-left">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">Competitive Edge</h2>
              <p className="text-lg text-muted-foreground mb-4">
                Leading real estate agents use digital business cards, gaining an edge by standing out as innovative,
                tech-forward, and client-ready.
              </p>
              <p className="text-lg text-muted-foreground mb-8">
                Give Your Real Estate Team a Competitive Advantage. Sign up for our Pilot Program!
              </p>
              <div className="flex flex-col items-center lg:items-start">
                <Button
                  className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-4 h-auto rounded-lg shadow-lg hover:shadow-xl"
                  onClick={() => document.getElementById("signup-section")?.scrollIntoView({ behavior: "smooth" })}
                >
                  Create Your Digital Card Free
                </Button>
                <div className="mt-3 flex flex-col sm:flex-row items-center gap-2 text-sm text-muted-foreground">
                  <span>No credit card required</span>
                  <span className="hidden sm:inline">•</span>
                  <span>Takes less than 2 minutes</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All Features. No Fees. */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              All Features. <span className="text-gradient">No Fees.</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Everything you need to create a professional digital presence — included free.
            </p>
          </div>

          <div className="hidden md:flex flex-col md:flex-row gap-8 md:gap-12 max-w-6xl mx-auto mt-8">
            <div className="w-full md:w-2/5">
              <div className="grid grid-cols-2 gap-3">
                {features.map((feature, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedFeature(index)}
                    className={`p-4 rounded-xl text-left transition-all duration-300 border ${selectedFeature === index ? "bg-enterprise-purple text-white border-enterprise-purple shadow-lg" : "bg-card border-border hover:border-enterprise-purple/50 hover:bg-muted/50"}`}
                  >
                    <feature.icon
                      className={`w-6 h-6 mb-2 ${selectedFeature === index ? "text-white" : "text-enterprise-purple"}`}
                    />
                    <h3
                      className={`font-semibold text-sm ${selectedFeature === index ? "text-white" : "text-foreground"}`}
                    >
                      {feature.title}
                    </h3>
                  </button>
                ))}
              </div>
            </div>

            <div className="w-full md:w-3/5">
              <div className="bg-muted/30 rounded-2xl overflow-hidden border border-border shadow-lg">
                {features[selectedFeature].isVideo ? (
                  <video
                    src={features[selectedFeature].image}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto object-contain"
                  />
                ) : (
                  <img
                    src={features[selectedFeature].image}
                    alt={features[selectedFeature].title}
                    className="w-full h-auto object-contain"
                  />
                )}
              </div>
              <div className="mt-6 text-center">
                <h3 className="text-xl md:text-2xl font-bold mb-3 text-foreground">
                  {features[selectedFeature].title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">{features[selectedFeature].description}</p>
              </div>
            </div>
          </div>

          <div className="md:hidden mt-12 space-y-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="bg-muted/30 rounded-2xl overflow-hidden border border-border shadow-lg mb-4">
                  {feature.isVideo ? (
                    <video
                      src={feature.image}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-auto object-contain"
                    />
                  ) : (
                    <img src={feature.image} alt={feature.title} className="w-full h-auto object-contain" />
                  )}
                </div>
                <h3 className="text-xl font-bold mb-2 text-foreground">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why QCard Is Built for Real Estate Professionals */}
      <section className="py-20 md:py-28 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Why QCard Is <span className="text-gradient">Built for Real Estate</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              In real estate, your brand is your business. QCard helps you stand out, stay memorable, and close more
              deals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
            <div className="bg-card border border-border rounded-2xl p-8 hover:border-enterprise-purple/30 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 rounded-xl bg-enterprise-purple/10 flex items-center justify-center mb-6">
                <Home className="w-7 h-7 text-enterprise-purple" />
              </div>
              <h3 className="font-bold text-xl mb-3">Every Showing Is a Chance to Connect</h3>
              <p className="text-muted-foreground">
                Buyers meet dozens of agents during their search. QCard ensures you're the one they remember — and can
                easily reach when they're ready to act.
              </p>
            </div>

            <div className="bg-card border border-border rounded-2xl p-8 hover:border-enterprise-purple/30 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 rounded-xl bg-enterprise-purple/10 flex items-center justify-center mb-6">
                <Briefcase className="w-7 h-7 text-enterprise-purple" />
              </div>
              <h3 className="font-bold text-xl mb-3">Professional First Impressions</h3>
              <p className="text-muted-foreground">
                Real estate is a trust business. A polished digital profile signals competence and attention to detail
                before you ever say a word.
              </p>
            </div>

            <div className="bg-card border border-border rounded-2xl p-8 hover:border-enterprise-purple/30 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 rounded-xl bg-enterprise-purple/10 flex items-center justify-center mb-6">
                <LineChart className="w-7 h-7 text-enterprise-purple" />
              </div>
              <h3 className="font-bold text-xl mb-3">Referrals Made Effortless</h3>
              <p className="text-muted-foreground">
                When a past client wants to recommend you, QCard makes it easy. One link shares your entire professional
                profile — no fumbling for business cards.
              </p>
            </div>
          </div>

          <div className="max-w-3xl mx-auto text-center mb-12 bg-gradient-to-br from-enterprise-purple/5 to-enterprise-purple/10 border border-enterprise-purple/20 rounded-2xl p-8">
            <p className="text-xl font-medium text-foreground mb-4">
              "The best listing agents aren't just found — they're remembered."
            </p>
            <p className="text-muted-foreground">
              Stop handing out paper cards that disappear. Start building a digital presence that keeps you top-of-mind
              when it matters most.
            </p>
            <Button
              size="lg"
              className="mt-6 bg-enterprise-purple hover:bg-enterprise-purple-dark text-white text-lg px-8 py-6 font-semibold shadow-lg shadow-enterprise-purple/30"
              onClick={() => document.getElementById("signup-section")?.scrollIntoView({ behavior: "smooth" })}
            >
              Create Your Free QCard
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>

          {/* Enterprise Section */}
          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">
              <Building2 className="w-4 h-4 inline mr-2" />
              Need QCard for your team?
            </p>
            <Button
              size="lg"
              className="bg-enterprise-purple hover:bg-enterprise-purple-dark text-white text-xl px-12 py-8 font-bold shadow-xl shadow-enterprise-purple/30"
              onClick={() => setIsEnterpriseExpanded(!isEnterpriseExpanded)}
            >
              Enterprise Plan Available
              <ChevronDown
                className={`ml-3 w-6 h-6 transition-transform duration-300 ${isEnterpriseExpanded ? "rotate-180" : ""}`}
              />
            </Button>

            <div
              className={`overflow-hidden transition-all duration-500 ease-in-out ${isEnterpriseExpanded ? "max-h-[2000px] opacity-100 mt-12" : "max-h-0 opacity-0 mt-0"}`}
            >
              <div className="max-w-3xl mx-auto text-center mb-12">
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  Designed for <span className="text-enterprise-purple">Enterprises</span>, from the Ground Up
                </h3>
                <p className="text-lg text-muted-foreground">
                  QCard provides the cutting edge features, infrastructure, security and support that enterprises need.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto text-left">
                {enterpriseFeatures.map((feature, index) => (
                  <div
                    key={index}
                    className="bg-card border border-border rounded-2xl p-6 hover:shadow-lg transition-all duration-300"
                  >
                    <div className={`w-14 h-14 rounded-2xl ${feature.color} flex items-center justify-center mb-4`}>
                      <feature.icon className="w-7 h-7" />
                    </div>
                    <h4 className="text-lg font-bold mb-3">{feature.title}</h4>
                    <ul className="space-y-2">
                      {feature.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start gap-2 text-muted-foreground text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-enterprise-purple mt-1.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Button
                  size="lg"
                  className="bg-enterprise-purple hover:bg-enterprise-purple-dark text-white text-lg px-8 py-6 font-semibold"
                  asChild
                >
                  <a href="mailto:info@qcardenterprise.com">
                    Contact Sales
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              How It <span className="text-gradient">Works</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">Get started in three simple steps</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full bg-enterprise-purple/10 flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-enterprise-purple">{step.step}</span>
                </div>
                <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Grid */}
      <ComparisonGridSection />

      {/* Testimonials */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              What Our <span className="text-gradient">Users Say</span>
            </h2>
          </div>

          <Carousel
            opts={{ align: "start", loop: true }}
            plugins={[Autoplay({ delay: 4000 })]}
            className="max-w-5xl mx-auto"
          >
            <CarouselContent>
              {testimonials.map((testimonial, index) => (
                <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
                  <a href={testimonial.link} target="_blank" rel="noopener noreferrer" className="block p-2">
                    <img
                      src={testimonial.image}
                      alt={`${testimonial.name} testimonial`}
                      className="rounded-xl shadow-lg hover:shadow-xl transition-shadow w-full"
                    />
                  </a>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex" />
            <CarouselNext className="hidden md:flex" />
          </Carousel>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-enterprise-purple/5 via-background to-orange-50/30">
        <div className="container mx-auto px-4 text-center">
          <div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Ready to Network with <span className="text-gradient">QCard</span>?
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands of real estate professionals who are making lasting impressions with QCard.
            </p>
            <Button
              size="lg"
              className="bg-enterprise-purple hover:bg-enterprise-purple-dark text-white text-lg px-8 py-6 font-semibold shadow-lg shadow-enterprise-purple/30"
              onClick={() => document.getElementById("signup-section")?.scrollIntoView({ behavior: "smooth" })}
            >
              Create Your Free QCard
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RealEstateDigitalBusinessCard;
